import React, { useCallback, useEffect, useMemo, useState } from "react";
import Header from "../../../component/organisms/header";
import MainLayout from "../../../component/atoms/layout/mainLayout";
import Sidebar from "../../../component/organisms/sidebar";
import DetailPageLayout from "../../../component/atoms/layout/detail/detailPageLayout";
import TableTitle from "./../../../component/atoms/text/table/tableTitle";
import styled from "styled-components";
import ExcelBtn from "../../../component/atoms/button/excelBtn";
import DatePicker from "../_components/datePicker";
import PretendardText from "../../../component/atoms/text/pretendardText";
import TableText from "./../../../component/atoms/text/table/tableText";
import { formatNumberWithCommas } from "./../../../util/formatNumber";
import Modal from "../../../component/templates/Modal";
import getRandomInt from "../../../util/getRandomInt";
import GenderAgeChart from "./_components/genderAgeChart";
import moment from "moment";
import AnalyticsApi from "../../../api/analytics.api";
import getToday from "../../../util/getToday";

const StatisticsGenderAge = () => {
  const today = useMemo(() => getToday(), []);

  const [popupIsOpen, setPopupIsOpen] = useState(false);

  const [selectedStartDate, setSelectedStartDate] = useState("");

  const [selectedEndDate, setSelectedEndDate] = useState("");

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalText, setModalText] = useState("");

  const [genderAgeData, setGenderAgeData] = useState([]);
  const [genderAgeTotalData, setGenderAgeTotalData] = useState({
    male: 0,
    female: 0,
    total: 0,
  });

  const reducer = useCallback((data, key) => {
    return data.reduce((acc, cur) => {
      return acc + Number(cur[key]);
    }, 0);
  }, []);

  const getGenderAgeData = async () => {
    if (selectedStartDate === "" && selectedEndDate !== "") {
      setModalIsOpen(true);
      setModalTitle("날짜 선택 오류");
      setModalText("시작 날짜를 선택해주세요.");
      setSelectedStartDate("");
    } else if (selectedEndDate !== "" && selectedStartDate > selectedEndDate) {
      setModalIsOpen(true);
      setModalTitle("날짜 선택 오류");
      setModalText("끝 날짜가 시작 날짜보다 빠를 수 없습니다.");
      setSelectedEndDate("");
    }

    try {
      const data = {
        page: 1,
        searchType: "period",
        searchDate: selectedEndDate
          ? moment(selectedEndDate).format("YYYY-MM-DD")
          : today,
        startDate: moment(selectedStartDate).format("YYYY-MM-DD"),
      };

      const response = (await AnalyticsApi.getListGenderAge(data)).data.data
        .content;

      // response에는 {ageGroup: '0-12세', gender: 'male', count: 0}, {ageGroup: '0-12세', gender: 'female', count: 0} 이런식으로 데이터가 온다.
      const processData = (data) => {
        const result = data.reduce((acc, item) => {
          // 이미 해당 ageGroup이 존재하는지 확인
          const existing = acc.find(
            (group) => group.ageGroup === item.ageGroup
          );

          if (existing) {
            // 존재하면 gender에 따라 count를 추가
            existing[item.gender] = (existing[item.gender] || 0) + item.count;
          } else {
            // 존재하지 않으면 새로운 객체 추가
            acc.push({
              ageGroup: item.ageGroup,
              [item.gender]: item.count, // gender에 따라 값을 설정
            });
          }

          return acc;
        }, []);

        return result;
      };

      const processedData = processData(response);

      setGenderAgeData(processedData);

      const maleTotalCount = reducer(processedData, "male");
      const femaleTotalCount = reducer(processedData, "female");

      const totalData = {
        male: maleTotalCount,
        female: femaleTotalCount,
        total: maleTotalCount + femaleTotalCount,
      };

      setGenderAgeTotalData(totalData);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <Header />
      <MainLayout
        style={{
          position: "relative",
          gap: 306,
        }}
      >
        <Sidebar />

        {/* 본문 영역 */}
        <DetailPageLayout>
          <TableTitle
            style={{
              marginBottom: 30,
            }}
          >
            성별·연령별 조회수
          </TableTitle>

          <ContentHeader>
            <DatePicker
              popupIsOpen={popupIsOpen}
              onClick={() => {
                setPopupIsOpen(!popupIsOpen);
              }}
              startDate={selectedStartDate}
              startDateHandler={(date) => {
                setSelectedStartDate(date);
              }}
              endDate={selectedEndDate}
              endDateHandler={(date) => {
                setSelectedEndDate(date);
              }}
              footerBtnHandler={() => {
                getGenderAgeData();
              }}
            />
            {/* <ExcelBtn /> */}
          </ContentHeader>

          <ContentContainer>
            <ChartContainer>
              <ChartHeader>
                <ChartHeaderText>차트 데이터</ChartHeaderText>
              </ChartHeader>
              <ChartGraphWrapper>
                <div
                  style={{
                    width: "fit-content",
                    height: 23,
                    display: "flex",
                    alignItems: "center",
                    gap: 20,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    <span
                      style={{
                        width: 16,
                        height: 16,
                        backgroundColor: "#009bde",
                        borderRadius: "50%",
                      }}
                    />
                    <PretendardText
                      style={{
                        color: "#aaa",
                        fontSize: "14px",
                        fontWeight: 600,
                        lineHeight: "22.75px",
                      }}
                    >
                      남
                    </PretendardText>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    <span
                      style={{
                        width: 16,
                        height: 16,
                        backgroundColor: "#cf6679",
                        borderRadius: "50%",
                      }}
                    />
                    <PretendardText
                      style={{
                        color: "#aaa",
                        fontSize: "14px",
                        fontWeight: 600,
                        lineHeight: "22.75px",
                      }}
                    >
                      여
                    </PretendardText>
                  </div>
                </div>
              </ChartGraphWrapper>
              <GenderAgeChart dataList={genderAgeData} />

              {/* 테이블 영역 */}
              <table
                style={{
                  width: 924,
                  alignSelf: "center",
                  borderSpacing: 0,
                  borderCollapse: "collapse",
                  marginBottom: 50,
                }}
              >
                <colgroup>
                  {/* 합계: 924px */}
                  {/* 연령 */}
                  <col width="330px" />
                  {/* 남 */}
                  <col width="318px" />
                  {/* 여 */}
                  <col width="276px" />
                </colgroup>
                <thead>
                  <tr
                    style={{
                      height: 41,
                      backgroundColor: "#313233",
                    }}
                  >
                    <th>
                      <TableText
                        style={{
                          fontWeight: "600",
                          lineHeight: "22.75px",
                          paddingLeft: 40,
                        }}
                      >
                        연령
                      </TableText>
                    </th>
                    <th>
                      <TableText
                        style={{
                          fontWeight: "600",
                          lineHeight: "22.75px",
                        }}
                      >
                        남
                      </TableText>
                    </th>
                    <th>
                      <TableText
                        style={{
                          fontWeight: "600",
                          lineHeight: "22.75px",
                        }}
                      >
                        여
                      </TableText>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    style={{
                      height: 39,
                      borderBottom: "0.5px solid rgba(255, 255, 255, 0.12)",
                    }}
                  >
                    <td>
                      <TableText
                        style={{
                          fontWeight: "700",
                          paddingLeft: 40,
                        }}
                      >
                        전체
                      </TableText>
                    </td>
                    <td>
                      <TableText
                        style={{
                          fontWeight: "700",
                        }}
                      >
                        {formatNumberWithCommas(genderAgeTotalData.male)}{" "}
                        <span
                          style={{
                            color: "#009bde",
                          }}
                        >
                          {genderAgeTotalData.total === 0
                            ? "(0%)"
                            : `(${(
                                (genderAgeTotalData.male /
                                  genderAgeTotalData.total) *
                                100
                              ).toFixed(0)}%)`}
                        </span>
                      </TableText>
                    </td>
                    <td>
                      <TableText
                        style={{
                          fontWeight: "700",
                          marginRight: 203,
                        }}
                      >
                        {formatNumberWithCommas(genderAgeTotalData.female)}{" "}
                        <span
                          style={{
                            color: "#cf6679",
                          }}
                        >
                          {genderAgeTotalData.total === 0
                            ? "(0%)"
                            : `(${(
                                (genderAgeTotalData.female /
                                  genderAgeTotalData.total) *
                                100
                              ).toFixed(0)}%)`}
                        </span>
                      </TableText>
                    </td>
                  </tr>
                  {genderAgeData.map((item, index) => {
                    return (
                      <>
                        <tr
                          key={index}
                          style={{
                            height: 39,
                            borderBottom:
                              "0.5px solid rgba(255, 255, 255, 0.12)",
                          }}
                        >
                          <td>
                            <TableText
                              style={{
                                paddingLeft: 40,
                              }}
                            >
                              {item?.ageGroup || ""}
                            </TableText>
                          </td>
                          <td>
                            <TableText style={{}}>
                              {formatNumberWithCommas(item.male)}{" "}
                              <span
                                style={{
                                  color: "#009bde",
                                }}
                              >
                                (
                                {item.male + item.female === 0
                                  ? 0
                                  : (
                                      (item.male / (item.male + item.female)) *
                                      100
                                    ).toFixed(0)}
                                %)
                              </span>
                            </TableText>
                          </td>
                          <td>
                            <TableText
                              style={{
                                marginRight: 203,
                              }}
                            >
                              {formatNumberWithCommas(item.female)}{" "}
                              <span
                                style={{
                                  color: "#cf6679",
                                }}
                              >
                                (
                                {item.male + item.female === 0
                                  ? 0
                                  : (
                                      (item.female /
                                        (item.male + item.female)) *
                                      100
                                    ).toFixed(0)}
                                %)
                              </span>
                            </TableText>
                          </td>
                        </tr>
                      </>
                    );
                  })}
                </tbody>
              </table>
            </ChartContainer>
          </ContentContainer>
        </DetailPageLayout>
      </MainLayout>

      {modalIsOpen && (
        <Modal
          type="alert"
          title={modalTitle}
          text={modalText}
          setModalOpen={setModalIsOpen}
          onClick={() => {
            setModalIsOpen(false);
          }}
        />
      )}
    </>
  );
};

export default StatisticsGenderAge;

const ContentContainer = styled.div.attrs((props) => {})`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: 24px;
`;

const ContentHeader = styled.div.attrs((props) => {})`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ChartContainer = styled.div.attrs((props) => {})`
  width: 1044px;
  height: fit-content;
  min-height: 439px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
  border-radius: 10px;
  background: #272829;
  box-shadow: 2px 2px 10px 0px rgba(0, 0, 0, 0.08);
`;

const ChartHeader = styled.div.attrs((props) => {})`
  width: 100%;
  height: 59px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0px 30px;
  border-bottom: 0.5px solid rgba(255, 255, 255, 0.12);
`;

const ChartHeaderText = styled(PretendardText).attrs((props) => {})`
  font-weight: 700;
  font-size: 16px;
  line-height: 28px;
  letter-spacing: -0.3%;
  color: #f3f3f3;
`;

const ChartGraphWrapper = styled.div.attrs((props) => {})`
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  padding: 24px 30px;
`;
